import { Navigate } from 'react-router-dom';
import type { RouteConfig } from '.';
import { routes } from './routes';

/**
 * 获取用户有权限访问的路由
 * @param permCodes 用户权限码数组(userStore获取)
 * @param data 路由数据(可选，默认使用routes)
 * @param filter 额外的路由过滤函数(可选，默认返回true)
 * @returns 有权限访问的路由数组
 */
export const getHasPermissionRoutes = (
  permCodes: string[],
  data?: RouteConfig[],
  filter: (route: RouteConfig) => boolean = () => true
): RouteConfig[] => {
  if (!permCodes.length) {
    const cloneRoute = (route: RouteConfig): RouteConfig => ({
      ...route,
      meta: route.meta ? { ...route.meta } : undefined,
      children: route.children ? route.children.map(cloneRoute) : undefined,
    });

    const loginRoute = routes.find((route) => route.path === '/login');
    const activateRoute = routes.find((route) => route.path === '/activate');
    const qrInvitationRoute = routes.find((route) => route.path === '/qr-invitation');
    const notFoundRoute = routes.find((route) => route.path === '/404');

    return [
      {
        path: '/',
        element: <Navigate to="/login" replace />,
      },
      ...(loginRoute ? [cloneRoute(loginRoute)] : []),
      ...(activateRoute ? [cloneRoute(activateRoute)] : []),
      ...(qrInvitationRoute ? [cloneRoute(qrInvitationRoute)] : []),
      ...(notFoundRoute ? [cloneRoute(notFoundRoute)] : []),
      {
        path: '*',
        element: <Navigate to="/login" replace />,
      },
    ];
  }
  const _routes: RouteConfig[] = data || routes;
  const eachArray = (routes: RouteConfig[]): RouteConfig[] => {
    return routes
      .map((route) => {
        // 创建新对象以避免修改原始路由配置
        const newRoute = { ...route };

        // 如果有 meta，也创建一个新对象，避免修改原始 meta
        if (newRoute.meta) {
          newRoute.meta = { ...newRoute.meta };
        }

        // 递归处理 children
        if (newRoute.children) {
          newRoute.children = eachArray(newRoute.children);
        }

        if (newRoute.permCode) {
          if (permCodes.includes(newRoute.permCode)) {
            return newRoute;
          }
        } else {
          // 没有权限码的路由视为非侧边栏导航跳转的路由，对于没有权限码的路由，不会在侧边栏导航中显示，但是可以通过直接访问路径来跳转
          return newRoute;
        }
      })
      .filter((r): r is RouteConfig => {
        if (!r) return false;
        return filter(r);
      });
  };
  return eachArray(_routes);
};
